/*
 * @license
 * Copyright© Celligence LLC - 2024
 *
 * This software program is protected by copyright laws and is the property of
 * Celligence LLC (Celligence). Any unauthorized reproduction,
 * distribution, modification, or public display of this program, without the
 * explicit permission of Celligence is strictly prohibited. If you have gained
 * access to this program without the expressed approval of Celligence report
 * the incident to legal@celligence.com.
 *
 */
import React, { Suspense, lazy, useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import NavBar from "../NavBar/NavBar";
import HeroSection from "../HeroSection/HeroSection";
import fallbackContent from "../content.json";
import "./MainPage.css";

// Lazy-load all below-the-fold sections
const ComparisonSection = lazy(() => import("../ComparisonSection/ComparisonSection"));
const ValueCards = lazy(() => import("../ValueCards/ValueCards"));
const PartnersSection = lazy(() => import("../PartnersSection/PartnersSection"));
const ContactForm = lazy(() => import("../ContactUs/Contactus"));
const DisclaimerSection = lazy(() => import("../DisclaimerSection/DisclaimerSection"));
const CustomFooter = lazy(() => import("../FooterNew/index"));

function MainPage() {
  const { loName } = useParams();
  const [content, setContent] = useState(fallbackContent);
  const [pageType, setPageType] = useState("homepage"); // "homepage" | "lo" | "cobranded"
  const [loData, setLoData] = useState(null);
  const [realtorData, setRealtorData] = useState(null);

  // Fetch common page content
  useEffect(() => {
    const fetchContent = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_CDN_PATH}homeownership.json?t=${Date.now()}`
        );
        setContent(res.data);
      } catch (error) {
        setContent(fallbackContent);
      }
    };
    fetchContent();
  }, []);

  // Determine page type and fetch relevant data
  useEffect(() => {
    if (!loName) {
      setPageType("homepage");
      return;
    }

    const fetchPageData = async () => {
      // Check if URL contains hyphen — cobranded page
      if (loName.includes("-")) {
        setPageType("cobranded");
        try {
          const cobrandedRes = await axios.get(
            `${process.env.REACT_APP_CDN_PATH}realtor-cobranded/${loName}.json?t=${Date.now()}`
          );
          if (cobrandedRes.data) {
            setRealtorData(cobrandedRes.data.realtor);

            // Fetch LO details using loJsonName
            if (cobrandedRes.data.loJsonName) {
              try {
                const loRes = await axios.get(
                  `${process.env.REACT_APP_MLO_JSONS}${cobrandedRes.data.loJsonName}.json?t=${Date.now()}`
                );
                const loArray = loRes.data;
                setLoData(Array.isArray(loArray) ? loArray[0] : loArray);
              } catch (loError) {
                setLoData(null);
              }
            }
          }
        } catch (error) {
          setRealtorData(null);
          setLoData(null);
        }
      } else {
        // No hyphen — LO page
        setPageType("lo");
        try {
          const loRes = await axios.get(
            `${process.env.REACT_APP_MLO_JSONS}${loName}.json?t=${Date.now()}`
          );
          const loArray = loRes.data;
          setLoData(Array.isArray(loArray) ? loArray[0] : loArray);
        } catch (loError) {
          setLoData(null);
        }
      }
    };

    fetchPageData();
  }, [loName]);

  return (
    <div className="main-page">
      <a href="#main-content" className="sr-only">
        Skip to main content
      </a>

      <NavBar data={content.navBar} pageType={pageType} />

      <main id="main-content" className="main-page__content">
        <HeroSection data={content.heroSection} />

        <Suspense fallback={<div style={{ minHeight: "600px" }} />}>
          <ComparisonSection data={content.comparisonSection} />
        </Suspense>

        <Suspense fallback={<div style={{ minHeight: "600px" }} />}>
          <ValueCards data={content.valueCards} />
        </Suspense>

        <Suspense fallback={<div style={{ minHeight: "600px" }} />}>
          <PartnersSection
            pageType={pageType}
            loData={loData}
            realtorData={realtorData}
            data={content.partnersSection}
          />
        </Suspense>

        <Suspense fallback={<div style={{ minHeight: "600px" }} />}>
          <ContactForm
            data={content.contactForm}
            pageType={pageType}
            loData={loData}
            realtorData={realtorData}
          />
        </Suspense>

        <Suspense fallback={<div style={{ minHeight: "80px" }} />}>
          <DisclaimerSection data={content.disclaimerSection} />
        </Suspense>
      </main>

      <Suspense fallback={<div style={{ minHeight: "400px", background: "#093f89" }} />}>
        <CustomFooter />
      </Suspense>
    </div>
  );
}

export default MainPage;
